/** Игрок: состояние, язык, право писать, отзыв. */
import { Empty } from "../errors";
import type { EndpointDef } from "../endpoint";
import { Locale, Platform, PosInt } from "../../common";
import { PlayerState } from "../player";
import { z } from "zod";

/** Выбор языка в игре: хранится у игрока и переживает перезаход. */
export const SetLocaleRequest = z.object({ locale: Locale });
export const SetLocaleResponse = z.object({ locale: Locale });

/** Тема отзыва из игры. */
export const FeedbackType = z.enum(["bug", "idea", "other"]);
export type FeedbackType = z.infer<typeof FeedbackType>;

export const FEEDBACK_TEXT_MIN = 3;
export const FEEDBACK_TEXT_MAX = 2000;
export const FEEDBACK_CONTACT_MAX = 100;

/** Отзыв игрока. `wave`, `platform` и `locale` подставляет клиент, версию и игрока — сервер. */
export const SendFeedbackRequest = z.object({
  type: FeedbackType,
  text: z.string().trim().min(FEEDBACK_TEXT_MIN).max(FEEDBACK_TEXT_MAX),
  contact: z.string().trim().max(FEEDBACK_CONTACT_MAX).optional(),
  wave: PosInt.nullable(),
  platform: Platform,
  locale: Locale,
});
export type SendFeedbackRequest = z.infer<typeof SendFeedbackRequest>;

/** Ответ на вопрос «может ли бот писать»: кнопка окна или системное окно `requestWriteAccess`. */
export const SetWriteAccessRequest = z.object({ allowed: z.boolean() });
export const SetWriteAccessResponse = z.object({ allowsWriteToPm: z.boolean() });

/** Кнопки ускорения админа: снять таймер после падения штаба, добавить монет. */
export const AdminToolRequest = z.object({ action: z.enum(["skip_timer", "coins"]) });
export type AdminToolRequest = z.infer<typeof AdminToolRequest>;

export const playerEndpoints = {
  /** Инструмент ускорения: только `ADMIN_TELEGRAM_IDS`, остальным 404. */
  adminTool: {
    method: "POST",
    path: "/api/player/admin-tool",
    request: AdminToolRequest,
    response: PlayerState,
    auth: true,
  },
  /** Текущее состояние игрока (офлайн-доход и серия пересчитываются на лету). */
  playerState: { method: "GET", path: "/api/player/state", request: Empty, response: PlayerState, auth: true },
  setLocale: {
    method: "POST",
    path: "/api/player/locale",
    request: SetLocaleRequest,
    response: SetLocaleResponse,
    auth: true,
  },
  setWriteAccess: {
    method: "POST",
    path: "/api/player/write-access",
    request: SetWriteAccessRequest,
    response: SetWriteAccessResponse,
    auth: true,
  },
  /** Отзыв из игры. Гость тоже игрок, поэтому нужна только сессия. */
  sendFeedback: { method: "POST", path: "/api/feedback", request: SendFeedbackRequest, response: Empty, auth: true },
} as const satisfies Record<string, EndpointDef>;
